import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money, TYPE_LABELS, catLabel } from "@/lib/format";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SiteEditor } from "@/components/SiteEditor";
import { AdminVerifications } from "@/components/AdminVerifications";

const Actions = ({ onAct, id }) => (
  <div className="flex gap-2">
    <button data-testid={`approve-${id}`} onClick={() => onAct("approved")} className="flex items-center gap-1 bg-emerald-600 text-white rounded-lg px-4 py-2 text-xs font-bold"><Check size={14} />موافقة</button>
    <button data-testid={`reject-${id}`} onClick={() => onAct("rejected")} className="flex items-center gap-1 bg-red-50 text-red-700 rounded-lg px-4 py-2 text-xs font-bold"><X size={14} />رفض</button>
  </div>
);
const Empty = () => <div className="text-center py-12 text-slate-500 bg-white border rounded-xl">لا يوجد عناصر بانتظار المراجعة.</div>;

const PendingListings = ({ items, act }) => !items.length ? <Empty /> : (
  <div className="space-y-3">
    {items.map((l) => (
      <div key={l.id} className="bg-white border rounded-xl p-4 flex flex-wrap gap-4 items-center justify-between" data-testid={`pending-listing-${l.id}`}>
        <div className="flex gap-3 items-center min-w-0">
          {l.image && <img src={l.image} alt="" className="w-20 h-16 rounded-lg object-cover" />}
          <div className="min-w-0">
            <Link to={`/listing/${l.id}`} className="font-bold hover:text-brand">{l.title}</Link>
            <div className="text-xs text-slate-500 mt-1"><span className="font-mono" dir="ltr">{l.ref_code}</span> · {TYPE_LABELS[l.type]} · {catLabel(l.type, l.category)} · {l.owner_display} · <span dir="ltr">{l.contact_phone}</span></div>
            <div className="text-xs text-slate-600 mt-1">{l.type === "auction" ? `افتتاح ${money(l.start_price)} · ${l.duration_days} يوم` : l.type === "job" ? l.salary : money(l.price)}</div>
          </div>
        </div>
        <Actions id={l.id} onAct={(s) => act(`/admin/listings/${l.id}/status`, s)} />
      </div>
    ))}
  </div>
);

const openCv = async (uid, lid) => {
  try {
    const { data } = await api.get(`/admin/cv/${uid}/${lid}`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    if (data.type === "application/pdf") a.target = "_blank";
    else a.download = `cv-${uid}.docx`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error(errMsg(e)); }
};

const PendingContacts = ({ items, act }) => !items.length ? <Empty /> : (
  <div className="space-y-3">
    {items.map((c) => (
      <div key={c.id} className="bg-white border rounded-xl p-4 flex flex-wrap gap-4 items-center justify-between" data-testid={`pending-contact-${c.id}`}>
        <div>
          <div className="font-bold">{c.requester_name}{c.requester_company ? ` — ${c.requester_company}` : ""} {c.requester_role && <span className="text-[11px] font-semibold bg-slate-100 text-slate-700 rounded-md px-1.5 py-0.5 mx-1" data-testid={`contact-role-${c.id}`}>{c.requester_role === "seller" ? "بائع" : "مشتري"}</span>}<span className="text-xs text-slate-500 font-normal">({c.requester_email}{c.requester_phone ? ` · ${c.requester_phone}` : ""})</span></div>
          <div className="text-sm text-slate-600 mt-1">يطلب التواصل بخصوص: <Link to={`/listing/${c.listing_id}`} className="text-brand font-semibold">{c.listing_title}</Link></div>
          {c.message && <div className="text-xs text-slate-500 mt-1">"{c.message}"</div>}
          {c.listing_type === "job" && (c.cv_score != null
            ? <button data-testid={`view-cv-${c.id}`} onClick={() => openCv(c.requester_id, c.listing_id)} className="text-xs font-bold text-brand mt-1">عرض السيرة الذاتية · توافق ATS {c.cv_score}/100</button>
            : <div className="text-xs text-slate-400 mt-1">لم يُرفق سيرة ذاتية</div>)}
        </div>
        <Actions id={c.id} onAct={(s) => act(`/admin/contact-requests/${c.id}/status`, s)} />
      </div>
    ))}
  </div>
);

const Commissions = ({ data }) => (
  <div className="space-y-4">
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="bg-brand text-white rounded-xl p-5"><div className="text-sm opacity-80">عمولات مستحقة (مزادات منتهية)</div><div className="text-3xl font-bold tracking-tight" data-testid="commission-due">{money(data.total_due)}</div></div>
      <div className="bg-[#FFF7ED] text-[#C2410C] rounded-xl p-5"><div className="text-sm">عمولات متوقعة (مزادات قائمة)</div><div className="text-3xl font-bold tracking-tight" data-testid="commission-expected">{money(data.total_expected)}</div></div>
    </div>
    <div className="bg-white border rounded-xl overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500"><tr><th className="p-3 text-right">المزاد</th><th className="p-3 text-right">أعلى سعر</th><th className="p-3 text-right">العمولة 1%</th><th className="p-3 text-right">الحالة</th><th className="p-3 text-right">الفائز</th></tr></thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.id} className="border-t" data-testid={`commission-row-${r.id}`}>
              <td className="p-3 font-semibold">{r.title}</td><td className="p-3">{money(r.current_bid)}</td>
              <td className="p-3 font-bold text-[#C2410C]">{money(r.commission)}</td><td className="p-3">{r.is_ended ? "منتهي" : "قائم"}</td>
              <td className="p-3 text-xs">{r.winner ? `${r.winner.company_name || r.winner.name} · ${r.winner.phone || r.winner.email}` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default function Admin() {
  const [listings, setListings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [comm, setComm] = useState({ rows: [], total_due: 0, total_expected: 0 });
  const [verCount, setVerCount] = useState(0);
  const load = useCallback(() => {
    api.get("/admin/listings").then((r) => setListings(r.data));
    api.get("/admin/contact-requests").then((r) => setContacts(r.data));
    api.get("/admin/commissions").then((r) => setComm(r.data));
    api.get("/admin/verifications").then((r) => setVerCount(r.data.length));
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (url, status) => {
    try { await api.post(url, { status }); toast.success(status === "approved" ? "تمت الموافقة" : "تم الرفض"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-page">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6">لوحة الإدارة</h1>
      <Tabs defaultValue="listings" dir="rtl">
        <TabsList className="rounded-lg flex-wrap h-auto">
          <TabsTrigger value="listings" data-testid="admin-tab-listings" className="rounded-lg">الإعلانات المعلقة ({listings.length})</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="admin-tab-contacts" className="rounded-lg">طلبات التواصل ({contacts.length})</TabsTrigger>
          <TabsTrigger value="commissions" data-testid="admin-tab-commissions" className="rounded-lg">العمولات</TabsTrigger>
          <TabsTrigger value="verifications" data-testid="admin-tab-verifications" className="rounded-lg">توثيق الحسابات ({verCount})</TabsTrigger>
          <TabsTrigger value="site" data-testid="admin-tab-site" className="rounded-lg">واجهة الموقع</TabsTrigger>
        </TabsList>
        <TabsContent value="listings" className="mt-5"><PendingListings items={listings} act={act} /></TabsContent>
        <TabsContent value="contacts" className="mt-5"><PendingContacts items={contacts} act={act} /></TabsContent>
        <TabsContent value="commissions" className="mt-5"><Commissions data={comm} /></TabsContent>
        <TabsContent value="verifications" className="mt-5"><AdminVerifications onCount={setVerCount} /></TabsContent>
        <TabsContent value="site" className="mt-5"><SiteEditor /></TabsContent>
      </Tabs>
    </div>
  );
}
